import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Clock } from 'lucide-react';
import CardItem from '../profile/CardItem';

const formatMatchDateTime = (dateStr, timeStr) => {
  const src = timeStr || dateStr;
  if (!src) return null;
  const d = new Date(src);
  if (isNaN(d.getTime())) return String(dateStr || '');
  const day = String(d.getDate()).padStart(2, '0');
  const mons = ['gen', 'feb', 'mar', 'apr', 'mag', 'giu', 'lug', 'ago', 'set', 'ott', 'nov', 'dic'];
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const tz =
    Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
      .formatToParts(d)
      .find((p) => p.type === 'timeZoneName')?.value || '';
  return tz ? `${day} ${mons[d.getMonth()]} ${hh}:${mm} (${tz})` : `${day} ${mons[d.getMonth()]} ${hh}:${mm}`;
};

const formatMatchInfo = (homeTeam, awayTeam, competitionName) => {
  const match = [homeTeam, awayTeam].filter(Boolean).join(' vs ');
  if (!match) return null;
  return competitionName ? `${match} • ${competitionName}` : match;
};

const TEAM_LEVEL_STAT_KEYS = new Set([
  'team_goals_scored',
  'team_goals_conceded',
  'team-goals-scored',
  'team-goals-conceded',
]);

const ScoreTooltipContent = ({
  detail = {},
  score,
  playerName,
  substituteInfo,
  substituteUnavailable,
}) => {
  const hasBreakdown = detail.breakdown && Array.isArray(detail.breakdown) && detail.breakdown.length > 0;
  const rawStats = detail.rawStats && typeof detail.rawStats === 'object' ? detail.rawStats : null;
  const rawEntries = rawStats
    ? Object.entries(rawStats).filter(
        ([k, v]) =>
          v != null &&
          v !== '' &&
          k.toLowerCase() !== 'rating' &&
          !TEAM_LEVEL_STAT_KEYS.has(k.replace(/-/g, '_'))
      )
    : [];
  const hasRaw = rawEntries.length > 0;
  const breakdownTotal = hasBreakdown
    ? detail.breakdown.reduce((sum, row) => sum + (Number(row.subtotal) || 0), 0)
    : null;
  const hasAnyRealData = hasBreakdown || hasRaw;

  return (
    <div
      className={`score-tooltip-box relative bg-gray-900 text-[10px] rounded-xl shadow-2xl p-2.5 ${
        hasAnyRealData ? 'w-64 max-w-[300px]' : 'w-44'
      }`}
    >
      <div
        className="absolute left-1/2 -bottom-[7px]"
        style={{
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '7px solid transparent',
          borderRight: '7px solid transparent',
          borderTop: '7px solid #111827',
        }}
      />

      {playerName && (
        <div className="font-bold text-center pb-1 mb-1 border-b border-white/10 truncate">{playerName}</div>
      )}

      {formatMatchInfo(detail.homeTeam, detail.awayTeam, detail.competitionName) && (
        <div className="text-center pb-1 mb-1 border-b border-white/10 text-[9px] leading-tight">
          ⚽ {formatMatchInfo(detail.homeTeam, detail.awayTeam, detail.competitionName)}
        </div>
      )}

      {formatMatchDateTime(detail.matchDate, detail.matchTime) && (
        <div className="text-center pb-1.5 mb-1.5 border-b border-white/10">
          📅 {formatMatchDateTime(detail.matchDate, detail.matchTime)}
        </div>
      )}

      {hasBreakdown && (
        <div className="mt-1.5 pt-1.5 border-t border-white/10">
          <div className="font-bold mb-0.5 text-[9px]">Mappatura score.md</div>
          <div className="grid grid-cols-[1fr_auto_2rem_2.5rem] gap-x-1 text-[8px] text-white/80 mb-0.5">
            <span>Evento</span>
            <span>Pts/u</span>
            <span className="text-right">Val</span>
            <span className="text-right">Sub</span>
          </div>
          <div className="space-y-0.5 max-h-28 overflow-y-auto text-[9px]">
            {detail.breakdown
              .slice()
              .sort((a, b) => Math.abs(b.subtotal || 0) - Math.abs(a.subtotal || 0))
              .map((row, i) => (
                <div key={i} className="flex justify-between gap-1">
                  <span className="truncate flex-1 min-w-0" title={row.event}>
                    {row.event}
                  </span>
                  <span className="shrink-0 tabular-nums">
                    {row.ptsPerUnit != null
                      ? (row.ptsPerUnit >= 0 ? '+' : '') + Number(row.ptsPerUnit).toFixed(2)
                      : '–'}
                  </span>
                  <span className="shrink-0 tabular-nums w-6 text-right">{row.value ?? '–'}</span>
                  <span className="shrink-0 tabular-nums w-10 text-right font-medium">
                    {row.subtotal != null
                      ? (row.subtotal >= 0 ? '+' : '') + Number(row.subtotal).toFixed(2)
                      : '–'}
                  </span>
                </div>
              ))}
          </div>
          {breakdownTotal != null && (
            <div className="grid grid-cols-[1fr_auto_2rem_2.5rem] gap-x-1 mt-0.5 pt-0.5 border-t border-white/20 text-[9px] font-bold">
              <span>Totale (base)</span>
              <span />
              <span />
              <span className="tabular-nums text-right">
                {(breakdownTotal >= 0 ? '+' : '') + Number(breakdownTotal).toFixed(2)}
              </span>
            </div>
          )}
          <div className="text-[7px] text-white/70 mt-0.5">
            &quot;Totale (base)&quot; = somma della colonna Sub sopra (eventi + eventuale minimo garantito da regole §9.5).
            Score finale = questo totale × bonus XP carta (se presente).
          </div>
        </div>
      )}

      {!hasBreakdown && detail.rawStats && Object.keys(detail.rawStats).length > 0 && rawEntries.length === 0 && (
        <div className="mt-1.5 pt-1.5 border-t border-white/10 text-[9px] text-amber-400/90">
          Nessuna statistica personale (solo dati di squadra)
        </div>
      )}
      {hasRaw && (
        <div className="mt-1.5 pt-1.5 border-t border-white/10">
          <div className="font-bold mb-0.5 text-[9px]">Statistiche Sportmonks</div>
          <div className="space-y-0.5 max-h-28 overflow-y-auto text-[9px]">
            {rawEntries
              .sort((a, b) => Math.abs(Number(b[1]) || 0) - Math.abs(Number(a[1]) || 0))
              .map(([key, val]) => (
                <div key={key} className="flex justify-between gap-2">
                  <span className="truncate flex-1 min-w-0">{key}</span>
                  <span className="shrink-0 tabular-nums">
                    {typeof val === 'number'
                      ? Number.isInteger(val)
                        ? val
                        : Number(val).toFixed(2)
                      : val}
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}

      {score != null && (
        <div className="mt-1.5 pt-1.5 border-t border-white/10 flex justify-between items-baseline">
          <span>Score finale</span>
          <span className="font-bold">{Number(score).toFixed(2)}</span>
        </div>
      )}

      {substituteInfo && (
        <div className="mt-1.5 pt-1.5 border-t border-amber-500/30 text-[9px]">
          <span className="text-amber-400 font-semibold">Sostituito da {substituteInfo.name}</span>
          <span className="text-white/90"> → +{Number(substituteInfo.score).toFixed(2)} al totale</span>
        </div>
      )}
      {substituteUnavailable && (
        <div className="mt-1.5 pt-1.5 border-t border-white/10 text-[9px] text-amber-400/90">
          Nessuna riserva disponibile per questo ruolo
        </div>
      )}
    </div>
  );
};

const MATCH_ENDED_THRESHOLD_MS = 3 * 60 * 60 * 1000;

/**
 * Quando mostrare il voto numerico come definitivo (badge verde, non giallo).
 * - Stato torneo/gameweek finale (closed, pending_payment, …).
 * - Oppure la finestra del torneo (end_date) è già passata: per gameweek vecchie la partita
 *   spesso non c’è più in api_match → niente matchDateTime → matchEnded resterebbe sempre false.
 */
export const tournamentScoresDisplayAsDefinitive = (tournament) => {
  if (!tournament) return false;
  const final = new Set(['closed', 'close', 'completed', 'cancelled', 'pending_payment']);
  const s = String(tournament.status || '').toLowerCase();
  const gw = String(tournament.gameweek_status || '').toLowerCase();
  if (final.has(s) || final.has(gw)) return true;
  if (tournament.end_date) {
    try {
      const end = new Date(tournament.end_date);
      if (!Number.isNaN(end.getTime()) && Date.now() > end.getTime()) return true;
    } catch (_) {
      /* ignore */
    }
  }
  return false;
};

const ScoreBadge = ({
  cardId,
  cardScores,
  cardDetails,
  liveNoData,
  notPlayed,
  matchDateTime,
  scoresAreFinal = false,
}) => {
  const score = cardScores[cardId];
  const detail = cardDetails[cardId];
  const numScore = score != null ? Number(score) : NaN;
  const hasNumericFromApi = score != null && score !== '' && !Number.isNaN(numScore);
  const played = detail?.played ?? (detail?.minutesPlayed > 0 || (detail?.breakdown?.length ?? 0) > 0);
  // cardScores viene da team_cards (affidabile). cardDetails può mancare se il JOIN su cards_* / match
  // non trova la riga (es. collisione id tra tabelle): in quel caso mostriamo comunque il voto se != 0.
  const hasScore =
    hasNumericFromApi &&
    (detail != null ? numScore !== 0 || played : numScore !== 0);
  const matchStarted =
    matchDateTime &&
    (() => {
      try {
        const t = new Date(matchDateTime).getTime();
        return !Number.isNaN(t) && t < Date.now();
      } catch (_) {
        return false;
      }
    })();
  const matchEnded =
    matchDateTime &&
    (() => {
      try {
        const t = new Date(matchDateTime).getTime();
        return !Number.isNaN(t) && Date.now() - t > MATCH_ENDED_THRESHOLD_MS;
      } catch (_) {
        return false;
      }
    })();
  // NP: notPlayed da backend, partita finita senza voto, oppure gameweek conclusa senza dato (niente più “in attesa” / grigio).
  const showNp =
    !hasScore && (notPlayed || matchEnded || scoresAreFinal);
  // matchEnded richiede matchDateTime da api_match; se il match non c’è più in DB, scoresAreFinal / end_date torneo coprono il caso.
  const isDefinitiveScore = hasScore && (scoresAreFinal || matchEnded);

  const bgClass = hasScore
    ? isDefinitiveScore
      ? 'bg-emerald-600 text-white'
      : 'bg-yellow-400 score-badge-white'
    : showNp
      ? 'bg-red-600 text-white'
      : liveNoData
        ? 'bg-amber-500/90 text-white'
        : matchStarted
          ? 'bg-emerald-600 text-white'
          : 'bg-gradient-to-r from-gray-500 to-gray-600 text-white';

  return (
    <div
      className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full shadow-lg border border-white/20 flex items-center justify-center gap-0.5 select-none ${bgClass}`}
    >
      {hasScore ? (
        <span>{score != null ? Number(score).toFixed(1) : '0.0'}</span>
      ) : showNp ? (
        <span className="text-[9px] font-bold">NP</span>
      ) : liveNoData ? (
        <>
          <Clock className="w-2.5 h-2.5 animate-pulse" />
          <span className="text-[9px]">Live - ...</span>
        </>
      ) : matchStarted ? (
        <>
          <Clock className="w-2.5 h-2.5 flex-shrink-0" />
          <span className="text-[9px]">Partita iniziata</span>
        </>
      ) : (
        <>
          <Clock className="w-2.5 h-2.5 animate-pulse" />
          <span className="text-[9px]">...</span>
        </>
      )}
    </div>
  );
};

function cardIdOf(card) {
  if (!card) return null;
  const id = card.id ?? card.card_id;
  return id != null ? id : null;
}

/** Map a deployed card back to its formation slot (for list-view taps). */
function resolveSlotForCard(team, cardId) {
  if (!team || cardId == null) return null;
  const matches = (c) => {
    const cid = cardIdOf(c);
    return cid != null && String(cid) === String(cardId);
  };
  if (matches(team.goalkeeper)) return { role: 'GK', index: 0 };
  const defIdx = (team.defenders || []).findIndex(matches);
  if (defIdx >= 0) return { role: 'DEF', index: defIdx };
  const midIdx = (team.midfielders || []).findIndex(matches);
  if (midIdx >= 0) return { role: 'MID', index: midIdx };
  const attIdx = (team.attackers || []).findIndex(matches);
  if (attIdx >= 0) return { role: 'ATT', index: attIdx };
  if (matches(team.reserves?.RES1)) return { role: 'RES1', index: null };
  if (matches(team.reserves?.RES2)) return { role: 'RES2', index: null };
  if (matches(team.reserves?.RES3)) return { role: 'RES3', index: null };
  if (matches(team.legend)) return { role: 'LEGEND', index: null };
  return null;
}

const FootballField = ({
  formation,
  team,
  cardScores = {},
  cardDetails = {},
  cardXp = {},
  cardLiveNoData = {},
  cardNotPlayed = {},
  cardMatchDateTime = {},
  cardMatchInfo = {},
  matchByPlayerId = {},
  totalScore,
  onPositionClick,
  onPlayerRemove,
  allowLegends = false,
  allowDivinities = false,
  user = null,
  usedCardsInActiveGameweeks,
  openTradesMap = {},
  onCardListed = () => {},
  scoresAreFinal = false,
}) => {
  const [showLineupListView, setShowLineupListView] = useState(false);
  const [hoveredCardId, setHoveredCardId] = useState(null);
  const [tooltipAnchor, setTooltipAnchor] = useState(null);
  const [pinnedCardId, setPinnedCardId] = useState(null);
  const [pinnedAnchor, setPinnedAnchor] = useState(null);
  const tooltipRef = useRef(null);
  const lastSlotTapRef = useRef(0);

  /** In edit mode (onPositionClick set) score badges must not steal taps on mobile. */
  const scoresInteractive = !onPositionClick;

  const activateSlot = (role, index = null, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!onPositionClick) return;
    const now = Date.now();
    if (now - lastSlotTapRef.current < 350) return;
    lastSlotTapRef.current = now;
    onPositionClick(role, index);
  };

  const usedCardsSet = useMemo(() => {
    if (usedCardsInActiveGameweeks == null) return new Set();
    if (usedCardsInActiveGameweeks instanceof Set) return usedCardsInActiveGameweeks;
    return new Set(Array.isArray(usedCardsInActiveGameweeks) ? usedCardsInActiveGameweeks : []);
  }, [usedCardsInActiveGameweeks]);

  const getXpValue = (card) => {
    const cardId = card?.id ?? card?.card_id;
    const xpEntry =
      cardId != null ? cardXp?.[cardId] ?? cardXp?.[String(cardId)] : undefined;
    let xpFromMap;
    if (xpEntry != null && typeof xpEntry === 'object') {
      xpFromMap = xpEntry.xp_percentage ?? xpEntry.xpPercentage;
    } else if (xpEntry != null) {
      xpFromMap = xpEntry;
    }
    const xpRaw = xpFromMap ?? card?.xp_percentage ?? card?.xpPercentage ?? 0;
    const xp = Number(xpRaw);
    return Number.isFinite(xp) ? xp : 0;
  };

  const getXpContribution = (finalScore, xpValue) => {
    const score = Number(finalScore);
    const xp = Number(xpValue);
    if (!Number.isFinite(score) || !Number.isFinite(xp) || xp === 0) return 0;
    return score - (score / (1 + xp / 100));
  };

  const handleEnter = (e, cardId) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setHoveredCardId(cardId);
    setTooltipAnchor({ left: rect.left, top: rect.top, width: rect.width, height: rect.height });
  };

  const handleLeave = () => {
    setHoveredCardId(null);
    if (!pinnedCardId) setTooltipAnchor(null);
  };

  const handleBadgeClick = (e, cardId) => {
    e.stopPropagation();
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    if (pinnedCardId === cardId) {
      setPinnedCardId(null);
      setPinnedAnchor(null);
    } else {
      setPinnedCardId(cardId);
      setPinnedAnchor({ left: rect.left, top: rect.top, width: rect.width, height: rect.height });
    }
  };

  useEffect(() => {
    if (!pinnedCardId) return;
    const onMouseDown = (ev) => {
      const target = ev.target;
      if (tooltipRef.current && tooltipRef.current.contains(target)) return;
      if (target.closest('[data-tooltip-trigger]')) return;
      setPinnedCardId(null);
      setPinnedAnchor(null);
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [pinnedCardId]);

  const parseFormation = (form) => {
    const parts = form.split('-').map(Number);
    if (parts.length === 3) {
      return { defenders: parts[0], midfielders: parts[1], attackers: parts[2] };
    }
    if (parts.length === 4) {
      return { defenders: parts[0], midfielders: parts[1] + parts[2], attackers: parts[3] };
    }
    if (parts.length === 5) {
      return { defenders: parts[0], midfielders: parts[1] + parts[2], attackers: parts[3] + parts[4] };
    }
    return { defenders: 4, midfielders: 3, attackers: 3 };
  };

  const formationLayout = parseFormation(formation);

  const getPlayerPositions = () => {
    const positions = [];
    positions.push({
      role: 'GK',
      x: 50,
      y: 92,
      player: team.goalkeeper,
      index: 0,
    });
    const defY = 75;
    const defSpacing = 100 / (formationLayout.defenders + 1);
    for (let i = 0; i < formationLayout.defenders; i++) {
      positions.push({
        role: 'DEF',
        x: defSpacing * (i + 1),
        y: defY,
        player: team.defenders[i] || null,
        index: i,
      });
    }
    const midY = 50;
    const midSpacing = 100 / (formationLayout.midfielders + 1);
    for (let i = 0; i < formationLayout.midfielders; i++) {
      positions.push({
        role: 'MID',
        x: midSpacing * (i + 1),
        y: midY,
        player: team.midfielders[i] || null,
        index: i,
      });
    }
    const attY = 25;
    const attSpacing = 100 / (formationLayout.attackers + 1);
    for (let i = 0; i < formationLayout.attackers; i++) {
      positions.push({
        role: 'ATT',
        x: attSpacing * (i + 1),
        y: attY,
        player: team.attackers[i] || null,
        index: i,
      });
    }
    return positions;
  };

  const playerPositions = getPlayerPositions();

  const lineupCardsInOrder = useMemo(() => {
    const list = [];
    if (team?.goalkeeper) list.push(team.goalkeeper);
    (team?.defenders || []).forEach((c) => c && list.push(c));
    (team?.midfielders || []).forEach((c) => c && list.push(c));
    (team?.attackers || []).forEach((c) => c && list.push(c));
    if (team?.reserves?.RES1) list.push(team.reserves.RES1);
    if (team?.reserves?.RES2) list.push(team.reserves.RES2);
    if (team?.reserves?.RES3) list.push(team.reserves.RES3);
    // Keep special slot card visible in list view as the final item
    if (team?.legend) {
      const legendId = team.legend.id ?? team.legend.card_id;
      const alreadyInList = list.some((c) => (c?.id ?? c?.card_id) === legendId);
      if (!alreadyInList) {
        list.push(team.legend);
      }
    }
    return list;
  }, [team]);

  const effectiveNotPlayed = (cardId) => {
    const sc = Number(cardScores[cardId]);
    if (!Number.isNaN(sc) && sc > 0) return false;
    const det = cardDetails[cardId];
    const played = det?.played ?? (det?.minutesPlayed > 0 || (det?.breakdown?.length ?? 0) > 0);
    if (played) return false;
    if (cardNotPlayed?.[cardId]) return true;
    const pos = playerPositions.find((p) => (p.player?.id ?? p.player?.card_id) === cardId);
    const mdt = cardMatchDateTime[cardId];
    if (!mdt) return false;
    try {
      const t = new Date(mdt).getTime();
      if (Number.isNaN(t)) return false;
      if (Date.now() - t <= MATCH_ENDED_THRESHOLD_MS) return false;
    } catch (_) {
      return false;
    }
    return true;
  };

  const getSubstituteReserveForNp = (cardId) => {
    if (!effectiveNotPlayed(cardId)) return null;
    let role = null;
    let npCountBeforeMe = 0;
    for (const pos of playerPositions) {
      const pid = pos.player?.id ?? pos.player?.card_id;
      if (pid === cardId) {
        role = pos.role;
        const sameRole = playerPositions.filter((p) => p.role === role);
        const myIdx = sameRole.findIndex((p) => (p.player?.id ?? p.player?.card_id) === cardId);
        npCountBeforeMe = sameRole
          .slice(0, myIdx)
          .filter((p) => effectiveNotPlayed(p.player?.id ?? p.player?.card_id)).length;
        break;
      }
    }
    if (!role || role === 'GK') return null;
    const slot = role === 'DEF' ? 'RES1' : role === 'MID' ? 'RES2' : 'RES3';
    const reserve = team?.reserves?.[slot];
    if (!reserve) return null;
    if (npCountBeforeMe > 0) return null;
    return reserve;
  };

  const getSubstituteInfoForNp = (cardId) => {
    const res = getSubstituteReserveForNp(cardId);
    if (!res) return null;
    const name = res.player?.display_name || res.player?.full_name || res.player?.last_name || 'Riserva';
    const score = Number(cardScores[res.id]) || 0;
    return { name, score };
  };

  const cardIdMatches = (card, cardId) => {
    if (!card || cardId == null) return false;
    const id = card.id ?? card.card_id;
    return id != null && String(id) === String(cardId);
  };

  const getTooltipData = (cardId) => {
    if (!cardId) return null;
    for (const pos of playerPositions) {
      if (cardIdMatches(pos.player, cardId)) {
        return {
          playerName:
            pos.player?.player?.display_name ||
            pos.player?.player?.full_name ||
            pos.player?.player?.last_name,
          playerId: pos.player?.player_id ?? pos.player?.player?.id,
          cardId: pos.player?.id ?? pos.player?.card_id ?? cardId,
        };
      }
    }
    const res =
      team.reserves &&
      [team.reserves.RES1, team.reserves.RES2, team.reserves.RES3].find((r) => cardIdMatches(r, cardId));
    if (res) {
      return {
        playerName: res.player?.display_name || res.player?.full_name || res.player?.last_name,
        playerId: res.player_id ?? res.player?.id,
        cardId: res.id ?? res.card_id ?? cardId,
      };
    }
    const lineupCard = lineupCardsInOrder.find((c) => cardIdMatches(c, cardId));
    if (lineupCard) {
      return {
        playerName:
          lineupCard.player?.display_name ||
          lineupCard.player?.full_name ||
          lineupCard.player?.last_name,
        playerId: lineupCard.player_id ?? lineupCard.player?.id,
        cardId: lineupCard.id ?? lineupCard.card_id ?? cardId,
      };
    }
    return null;
  };

  const activeCardId = pinnedCardId ?? hoveredCardId;
  const activeAnchor = pinnedCardId ? pinnedAnchor : tooltipAnchor;
  const tooltipData = activeCardId ? getTooltipData(activeCardId) : null;
  const isPinned = !!pinnedCardId;

  const renderPortalTooltip = () => {
    if (!activeCardId || !activeAnchor || !tooltipData) return null;
    const { cardId, playerName } = tooltipData;
    const detail = cardDetails[cardId];
    const score = cardScores[cardId];
    const ld = cardLiveNoData[cardId];
    const mi = cardMatchInfo[cardId];
    const dtStr = ld?.matchDateTime || cardMatchDateTime[cardId];
    const subInfo = effectiveNotPlayed(cardId) ? getSubstituteInfoForNp(cardId) : null;

    const content = detail ? (
      <ScoreTooltipContent
        detail={detail}
        score={score}
        playerName={playerName}
        substituteInfo={subInfo}
        substituteUnavailable={effectiveNotPlayed(cardId) && !subInfo}
      />
    ) : (
      <div className="score-tooltip-box relative w-44 bg-gray-900 text-[10px] rounded-xl shadow-2xl p-2.5">
        <div className="font-bold text-center">{playerName}</div>
        {(() => {
          const matchStr = formatMatchInfo(
            ld?.homeTeam ?? mi?.homeTeam,
            ld?.awayTeam ?? mi?.awayTeam,
            ld?.competitionName ?? mi?.competitionName
          );
          return matchStr ? <div className="text-center text-[9px] mt-1">⚽ {matchStr}</div> : null;
        })()}
        <div className="text-center text-xs mt-1">
          {effectiveNotPlayed(cardId)
            ? subInfo ? (
              <>
                Sostituito da <span className="font-semibold text-amber-400">{subInfo.name}</span> → +
                {Number(subInfo.score).toFixed(2)} al totale
              </>
            ) : (
              'Nessuna riserva disponibile per questo ruolo'
            )
            : ld && !scoresAreFinal
              ? 'Live - attesa dati...'
              : scoresAreFinal
                ? 'NP — gameweek conclusa, nessun voto disponibile'
                : 'Dati non ancora disponibili'}
        </div>
        {dtStr ? <div className="text-[10px] text-center mt-1">📅 {formatMatchDateTime(null, dtStr)}</div> : null}
      </div>
    );
    const x = activeAnchor.left + activeAnchor.width / 2;
    const y = activeAnchor.top;
    return createPortal(
      <div
        ref={tooltipRef}
        className={`score-tooltip-box fixed z-[99999] ${isPinned ? 'pointer-events-auto' : 'pointer-events-none'}`}
        style={{ left: x, top: y, transform: 'translate(-50%, -100%) translateY(-8px)' }}
      >
        {content}
      </div>,
      document.body
    );
  };

  return (
    <>
      {renderPortalTooltip()}
      <div className="formation-view-switch-wrap flex flex-col items-center gap-1.5 mb-2" style={{ maxWidth: '1000px', margin: '0 auto' }}>
        <div className="formation-view-switch-row w-full">
          <span className={`formation-view-switch-label ${!showLineupListView ? 'is-active' : ''}`}>Campo</span>
          <button
            type="button"
            role="switch"
            aria-checked={showLineupListView}
            aria-label={
              showLineupListView ? 'Switch: passa alla vista campo' : 'Switch: passa alla vista lista'
            }
            title="Switch"
            className="formation-view-switch"
            onClick={() => setShowLineupListView((v) => !v)}
          >
            <span className="formation-view-switch-thumb" />
          </button>
          <span className={`formation-view-switch-label ${showLineupListView ? 'is-active' : ''}`}>Lista</span>
        </div>
      </div>

      <div className={`relative w-full flex flex-col gap-4 ${showLineupListView ? '' : 'md:flex-row'}`} style={{ maxWidth: '1000px', margin: '0 auto' }}>
        <div
          className={`football-field-area relative w-full ${showLineupListView ? '' : 'md:w-2/3'}`}
          style={
            showLineupListView ? { minHeight: 'auto' } : { aspectRatio: '2/3', minHeight: '500px' }
          }
        >
          {showLineupListView ? (
            <div className="lineup-list-container w-full rounded-lg border-2 border-cyan-400/30 bg-gradient-to-b from-slate-900/90 to-black/90 p-3 min-h-[360px] flex flex-col">
              {lineupCardsInOrder.length === 0 ? (
                <div className="flex-1 flex items-center justify-center text-white/80 text-sm font-medium px-4 text-center">
                  Nessuna carta schierata. Torna al campo per assegnare i giocatori.
                </div>
              ) : (
                <div className="formation-lineup-carousel flex gap-3 sm:gap-4 overflow-x-auto pb-3 snap-x snap-mandatory scroll-smooth [-webkit-overflow-scrolling:touch]">
                  {lineupCardsInOrder.map((card, index) => {
                    const cardId = card?.id ?? card?.card_id;
                    const playerId = card?.player_id ?? card?.player?.id;
                    const isReserveCard =
                      team?.reserves?.RES1?.id === cardId ||
                      team?.reserves?.RES2?.id === cardId ||
                      team?.reserves?.RES3?.id === cardId;
                    const cardForListView = card?.player?.display_name
                      ? {
                          ...card,
                          player: {
                            ...card.player,
                            full_name: card.player.display_name,
                          },
                        }
                      : card;
                    const mInfo = cardMatchInfo?.[cardId] || (playerId ? matchByPlayerId?.[playerId] : null);
                    const mDate = cardMatchDateTime?.[cardId] || (playerId ? matchByPlayerId?.[playerId]?.matchDateTime : null);
                    const matchLabel = formatMatchInfo(mInfo?.homeTeam, mInfo?.awayTeam, mInfo?.competitionName);
                    const whenLabel = formatMatchDateTime(null, mDate);
                    const xpLabel = `${getXpValue(card).toFixed(1)}%`;
                    const cardSlot = resolveSlotForCard(team, cardId);
                    const cardShell = (
                      <div className="lineup-card-shell rounded-xl border border-cyan-400/40 bg-black/60 shadow-[0_0_20px_rgba(0,217,255,0.2)]">
                          <div className="relative profile-gallery-card-scaled">
                            <CardItem
                              card={cardForListView}
                              isUsedInActiveGameweek={usedCardsSet.has(cardId)}
                              user={user}
                              onCardListed={onCardListed}
                              hasOpenTrade={!!openTradesMap[cardId]}
                              openTrade={openTradesMap[cardId]}
                              hideHoverActions
                              hideSellCart
                            />
                          </div>
                          <div className="p-2.5 space-y-1.5">
                            <div className="text-white/70 text-[11px] truncate">{matchLabel || 'Partita non disponibile'}</div>
                            {whenLabel && <div className="text-white/60 text-[10px] truncate">📅 {whenLabel}</div>}
                            <div className="pt-1 flex items-center justify-center">
                              {scoresInteractive ? (
                                <div
                                  data-tooltip-trigger
                                  className="flex flex-col items-center cursor-pointer"
                                  onMouseEnter={(e) => handleEnter(e, cardId)}
                                  onMouseLeave={handleLeave}
                                  onClick={(e) => handleBadgeClick(e, cardId)}
                                >
                                  <ScoreBadge
                                    cardId={cardId}
                                    cardScores={cardScores}
                                    cardDetails={cardDetails}
                                    liveNoData={cardLiveNoData?.[cardId]}
                                    notPlayed={effectiveNotPlayed(cardId)}
                                    matchDateTime={mDate}
                                    scoresAreFinal={scoresAreFinal}
                                  />
                                </div>
                              ) : (
                                <ScoreBadge
                                  cardId={cardId}
                                  cardScores={cardScores}
                                  cardDetails={cardDetails}
                                  liveNoData={cardLiveNoData?.[cardId]}
                                  notPlayed={effectiveNotPlayed(cardId)}
                                  matchDateTime={mDate}
                                  scoresAreFinal={scoresAreFinal}
                                />
                              )}
                              {isReserveCard && (
                                <span className="ml-2 text-[10px] font-bold tracking-wide text-cyan-300">- RES</span>
                              )}
                            </div>
                            <div className="text-center text-[10px] font-semibold text-emerald-300">XP {xpLabel}</div>
                          </div>
                        </div>
                    );

                    return (
                      <div
                        key={cardId || playerId || `lineup-${index}`}
                        className="lineup-carousel-item shrink-0 snap-center first:pl-1 last:pr-1 w-[227px] sm:w-[260px] md:w-[303px]"
                      >
                        {onPositionClick && cardSlot ? (
                          <button
                            type="button"
                            className="formation-lineup-card-btn w-full text-left touch-manipulation"
                            onClick={(e) => activateSlot(cardSlot.role, cardSlot.index, e)}
                          >
                            {cardShell}
                          </button>
                        ) : (
                          cardShell
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className={`football-field-pitch absolute inset-0 bg-gradient-to-b from-green-600 via-green-500 to-green-600 rounded-lg border-4 border-white shadow-2xl ${scoresInteractive ? 'football-field-pitch--scores' : ''}`}>
              <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
                <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-white/70" />
                <div
                  className="football-field-center-circle absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 border-2 border-white/70"
                  style={{ width: '176px', height: '176px' }}
                />
                <div className="football-field-center-dot absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-white" />
                <div
                  className="absolute top-0 left-1/2 transform -translate-x-1/2 w-3/4 h-1/4 border-2 border-white/70"
                  style={{ borderRadius: 0 }}
                >
                  <div
                    className="absolute top-0 left-1/2 transform -translate-x-1/2 border-2 border-white/70"
                    style={{ borderRadius: 0, width: '43.3%', height: '50%' }}
                  />
                </div>
                <div
                  className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-3/4 h-1/4 border-2 border-white/70"
                  style={{ borderRadius: 0 }}
                >
                  <div
                    className="absolute bottom-0 left-1/2 transform -translate-x-1/2 border-2 border-white/70"
                    style={{ borderRadius: 0, width: '43.3%', height: '50%' }}
                  />
                </div>
              </div>

              {playerPositions.map((pos) => (
                <button
                  type="button"
                  key={`${pos.role}-${pos.index}`}
                  className="formation-field-slot absolute -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-all hover:scale-110 z-20 touch-manipulation"
                  style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
                  aria-label={
                    pos.player
                      ? `Modifica ${pos.role}: ${pos.player.player?.display_name || pos.player.player?.last_name || 'Giocatore'}`
                      : `Seleziona ${pos.role}`
                  }
                  onClick={(e) => activateSlot(pos.role, pos.index, e)}
                >
                  <div className="football-field-slot-stack pointer-events-none">
                    {pos.player && scoresInteractive && (
                      <div
                        data-tooltip-trigger
                        className="flex flex-col items-center cursor-pointer pointer-events-auto z-20 shrink-0"
                        onMouseEnter={(e) => handleEnter(e, pos.player.id)}
                        onMouseLeave={handleLeave}
                        onClick={(e) => handleBadgeClick(e, pos.player.id)}
                      >
                        <ScoreBadge
                          cardId={pos.player.id}
                          cardScores={cardScores}
                          cardDetails={cardDetails}
                          liveNoData={cardLiveNoData[pos.player.id]}
                          notPlayed={effectiveNotPlayed(pos.player.id)}
                          matchDateTime={cardMatchDateTime[pos.player.id]}
                          scoresAreFinal={scoresAreFinal}
                        />
                      </div>
                    )}
                    <div
                      className={`w-20 h-14 shrink-0 transform rotate-45 bg-gradient-to-br ${
                        pos.player
                          ? pos.role === 'GK'
                            ? 'from-blue-500 to-blue-700'
                            : pos.role === 'DEF'
                              ? 'from-green-500 to-green-700'
                              : pos.role === 'MID'
                                ? 'from-purple-500 to-purple-700'
                                : 'from-red-500 to-red-700'
                          : 'from-gray-400 to-gray-600 opacity-40'
                      } rounded shadow-lg border-2 border-white flex items-center justify-center transition-all hover:shadow-xl`}
                    >
                      <div className="transform -rotate-45 text-white text-xs font-bold text-center px-1">
                        {pos.player ? (
                          <div>
                            <div className="text-[10px] leading-tight font-semibold">
                              {pos.player.player?.display_name ||
                                pos.player.player?.last_name ||
                                pos.player.player?.full_name?.split(' ').slice(-1)[0] ||
                                'Player'}
                            </div>
                            <div className="text-[8px] opacity-90 mt-0.5">{pos.role}</div>
                          </div>
                        ) : (
                          <div className="text-[10px] opacity-60">{pos.role}</div>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {!showLineupListView && (
          <div className="w-full md:w-1/3 flex flex-col gap-3">
            <>
          <h3 className="bench-reserves-title text-white font-bold text-lg text-center mb-2">Bench (Reserves)</h3>

          <button
            type="button"
            className={`formation-field-slot football-card relative p-4 w-full text-left cursor-pointer transition-all hover:scale-105 touch-manipulation ${
              team.reserves?.RES1 ? 'ring-2 ring-green-400 bg-green-400/20' : 'bg-white/50 border border-[rgba(65,105,225,0.2)]'
            }`}
            onClick={(e) => activateSlot('RES1', null, e)}
          >
            <div
              className="reserve-role-label font-semibold text-sm pointer-events-none"
            >
              RES1 (Defender)
            </div>
            {team.reserves?.RES1 ? (
              <div className="football-field-slot-stack pointer-events-none mt-4">
                {scoresInteractive && (
                  <div
                    data-tooltip-trigger
                    className="flex flex-col items-center cursor-pointer pointer-events-auto z-20 shrink-0"
                    onMouseEnter={(e) => handleEnter(e, team.reserves.RES1.id)}
                    onMouseLeave={handleLeave}
                    onClick={(e) => handleBadgeClick(e, team.reserves.RES1.id)}
                  >
                    <ScoreBadge
                      cardId={team.reserves.RES1.id}
                      cardScores={cardScores}
                      cardDetails={cardDetails}
                      liveNoData={cardLiveNoData[team.reserves.RES1.id]}
                      notPlayed={cardNotPlayed[team.reserves.RES1.id]}
                      matchDateTime={cardMatchDateTime[team.reserves.RES1.id]}
                      scoresAreFinal={scoresAreFinal}
                    />
                  </div>
                )}
                <div className="w-full h-20 shrink-0 transform rotate-45 bg-gradient-to-br from-green-500 to-green-700 rounded shadow-lg border-2 border-white flex items-center justify-center">
                  <div className="transform -rotate-45 text-white text-xs font-bold text-center px-1">
                    <div className="text-[10px] leading-tight font-semibold">
                      {team.reserves.RES1.player?.display_name ||
                        team.reserves.RES1.player?.last_name ||
                        team.reserves.RES1.player?.full_name?.split(' ').slice(-1)[0] ||
                        'Player'}
                    </div>
                    <div className="text-[8px] opacity-90 mt-0.5">RES1</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="w-full h-20 transform rotate-45 bg-gradient-to-br from-gray-400 to-gray-600 opacity-40 rounded shadow-lg border-2 border-white flex items-center justify-center">
                <div className="transform -rotate-45 text-white text-xs opacity-60">Empty</div>
              </div>
            )}
          </button>

          <button
            type="button"
            className={`formation-field-slot football-card relative p-4 w-full text-left cursor-pointer transition-all hover:scale-105 touch-manipulation ${
              team.reserves?.RES2
                ? 'ring-2 ring-purple-400 bg-purple-400/20'
                : 'bg-white/50 border border-[rgba(65,105,225,0.2)]'
            }`}
            onClick={(e) => activateSlot('RES2', null, e)}
          >
            <div
              className="reserve-role-label font-semibold text-sm pointer-events-none"
            >
              RES2 (Midfielder)
            </div>
            {team.reserves?.RES2 ? (
              <div className="football-field-slot-stack pointer-events-none mt-4">
                {scoresInteractive && (
                  <div
                    data-tooltip-trigger
                    className="flex flex-col items-center cursor-pointer pointer-events-auto z-20 shrink-0"
                    onMouseEnter={(e) => handleEnter(e, team.reserves.RES2.id)}
                    onMouseLeave={handleLeave}
                    onClick={(e) => handleBadgeClick(e, team.reserves.RES2.id)}
                  >
                    <ScoreBadge
                      cardId={team.reserves.RES2.id}
                      cardScores={cardScores}
                      cardDetails={cardDetails}
                      liveNoData={cardLiveNoData[team.reserves.RES2.id]}
                      notPlayed={cardNotPlayed[team.reserves.RES2.id]}
                      matchDateTime={cardMatchDateTime[team.reserves.RES2.id]}
                      scoresAreFinal={scoresAreFinal}
                    />
                  </div>
                )}
                <div className="w-full h-20 shrink-0 transform rotate-45 bg-gradient-to-br from-purple-500 to-purple-700 rounded shadow-lg border-2 border-white flex items-center justify-center">
                  <div className="transform -rotate-45 text-white text-xs font-bold text-center px-1">
                    <div className="text-[10px] leading-tight font-semibold">
                      {team.reserves.RES2.player?.display_name ||
                        team.reserves.RES2.player?.last_name ||
                        team.reserves.RES2.player?.full_name?.split(' ').slice(-1)[0] ||
                        'Player'}
                    </div>
                    <div className="text-[8px] opacity-90 mt-0.5">RES2</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="w-full h-20 transform rotate-45 bg-gradient-to-br from-gray-400 to-gray-600 opacity-40 rounded shadow-lg border-2 border-white flex items-center justify-center">
                <div className="transform -rotate-45 text-white text-xs opacity-60">Empty</div>
              </div>
            )}
          </button>

          <button
            type="button"
            className={`formation-field-slot football-card relative p-4 w-full text-left cursor-pointer transition-all hover:scale-105 touch-manipulation ${
              team.reserves?.RES3 ? 'ring-2 ring-red-400 bg-red-400/20' : 'bg-white/50 border border-[rgba(65,105,225,0.2)]'
            }`}
            onClick={(e) => activateSlot('RES3', null, e)}
          >
            <div
              className="reserve-role-label font-semibold text-sm pointer-events-none"
            >
              RES3 (Attacker)
            </div>
            {team.reserves?.RES3 ? (
              <div className="football-field-slot-stack pointer-events-none mt-4">
                {scoresInteractive && (
                  <div
                    data-tooltip-trigger
                    className="flex flex-col items-center cursor-pointer pointer-events-auto z-20 shrink-0"
                    onMouseEnter={(e) => handleEnter(e, team.reserves.RES3.id)}
                    onMouseLeave={handleLeave}
                    onClick={(e) => handleBadgeClick(e, team.reserves.RES3.id)}
                  >
                    <ScoreBadge
                      cardId={team.reserves.RES3.id}
                      cardScores={cardScores}
                      cardDetails={cardDetails}
                      liveNoData={cardLiveNoData[team.reserves.RES3.id]}
                      notPlayed={cardNotPlayed[team.reserves.RES3.id]}
                      matchDateTime={cardMatchDateTime[team.reserves.RES3.id]}
                      scoresAreFinal={scoresAreFinal}
                    />
                  </div>
                )}
                <div className="w-full h-20 shrink-0 transform rotate-45 bg-gradient-to-br from-red-500 to-red-700 rounded shadow-lg border-2 border-white flex items-center justify-center">
                  <div className="transform -rotate-45 text-white text-xs font-bold text-center px-1">
                    <div className="text-[10px] leading-tight font-semibold">
                      {team.reserves.RES3.player?.display_name ||
                        team.reserves.RES3.player?.last_name ||
                        team.reserves.RES3.player?.full_name?.split(' ').slice(-1)[0] ||
                        'Player'}
                    </div>
                    <div className="text-[8px] opacity-90 mt-0.5">RES3</div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="w-full h-20 transform rotate-45 bg-gradient-to-br from-gray-400 to-gray-600 opacity-40 rounded shadow-lg border-2 border-white flex items-center justify-center">
                <div className="transform -rotate-45 text-white text-xs opacity-60">Empty</div>
              </div>
            )}
          </button>
            </>

          <div className="text-left text-white text-sm font-semibold mt-2">
            Formation: {formation} ({formationLayout.defenders}-{formationLayout.midfielders}-
            {formationLayout.attackers})
          </div>

          {(() => {
            const starters = [
              team.goalkeeper,
              ...(team.defenders || []),
              ...(team.midfielders || []),
              ...(team.attackers || []),
            ].filter(Boolean);
            const allCards = [
              ...starters,
              team.reserves?.RES1,
              team.reserves?.RES2,
              team.reserves?.RES3,
              team.legend,
            ].filter(Boolean);
            const allContributors = starters.map((p) => {
              const np = effectiveNotPlayed(p.id);
              const sub = getSubstituteReserveForNp(p.id);
              const effectiveCard = np && sub ? sub : p;
              const name =
                effectiveCard.player?.display_name ||
                effectiveCard.player?.full_name ||
                effectiveCard.player?.last_name ||
                'Giocatore';
              const score = Number(cardScores[effectiveCard.id]) || 0;
              const xp = getXpValue(effectiveCard);
              const xpContribution = getXpContribution(score, xp);
              return { name, score, xp, xpContribution, isSub: np && !!sub, isNpNoSub: np && !sub };
            });
            const total = allContributors.reduce((sum, c) => sum + c.score, 0);
            const contributors = allContributors.filter((c) => !c.isNpNoSub);
            const specialCardsPool = [
              ...allCards,
              team.legend,
            ].filter(Boolean);
            const getSpecialCard = (kind) => specialCardsPool.find((c) => {
              const k = String(c?.card_kind || c?.cardKind || '').toLowerCase();
              const r = String(c?.rarity?.name || '').toLowerCase();
              const playerFlag = kind === 'legend' ? !!c?.player?.is_legend : !!c?.player?.is_divinity;
              return k === kind || r === kind || playerFlag;
            }) || null;
            const legendCard = getSpecialCard('legend');
            const divinityCard = getSpecialCard('divinity');
            const specialCardName = (card) =>
              card?.player?.display_name ||
              card?.player?.full_name ||
              card?.player?.last_name ||
              card?.custom_name ||
              card?.customName ||
              'Slot vuoto';
            const showSpecialSlots = allowDivinities || allowLegends;
            if (starters.length === 0 && allCards.length === 0 && !showSpecialSlots) return null;
            return (
              <div className="text-center text-white mt-4 pt-3 border-t border-white/20 space-y-1">
                {contributors.length > 0 && (
                  <div className="text-left text-sm text-white/90 mb-2 space-y-0.5">
                    {contributors.map((c, i) => (
                      <div key={i} className="flex justify-between gap-3 font-mono">
                        <span className="truncate flex-1 min-w-0">
                          {c.name}
                          {c.isSub ? ' (riserva)' : ''}
                        </span>
                        <span className="shrink-0 text-right leading-tight">
                          <div className="tabular-nums">{Number(c.score) >= 0 ? '+' : ''}{Number(c.score).toFixed(2)}</div>
                          <div className="text-[10px] text-white/70">
                            XP {Number(c.xp).toFixed(1)}% ({Number(c.xpContribution) >= 0 ? '+' : ''}{Number(c.xpContribution).toFixed(2)})
                          </div>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                {starters.length > 0 && (
                  <div className="font-bold text-lg">
                    Totale squadra:{' '}
                    {totalScore != null && totalScore !== ''
                      ? Number(totalScore).toFixed(2)
                      : Number(total).toFixed(2)}
                  </div>
                )}
                {showSpecialSlots && (
                  <div className="pt-2 mt-2 border-t border-white/10 grid grid-cols-1 gap-2">
                    {allowDivinities && (
                      <button
                        type="button"
                        className={`w-full rounded-md border px-2 py-1 text-left transition-all ${
                          divinityCard
                            ? 'border-fuchsia-400/80 bg-fuchsia-500/15 hover:bg-fuchsia-500/25'
                            : 'border-fuchsia-400/60 bg-fuchsia-500/10 hover:bg-fuchsia-500/20'
                        } ${onPositionClick && onPlayerRemove ? 'cursor-pointer' : 'cursor-default'}`}
                        onClick={() => {
                          if (!onPositionClick) return;
                          onPositionClick('DIVINITY');
                        }}
                        title={divinityCard ? 'Click per cambiare Divinity' : 'Click per selezionare Divinity'}
                      >
                        <div className="text-[10px] uppercase tracking-wide text-fuchsia-300 font-bold">Divinity</div>
                        <div className="text-xs text-white truncate">{specialCardName(divinityCard)}</div>
                      </button>
                    )}
                    {allowLegends && (
                      <button
                        type="button"
                        className={`w-full rounded-md border px-2 py-1 text-left transition-all ${
                          legendCard
                            ? 'border-yellow-400/80 bg-yellow-500/15 hover:bg-yellow-500/25'
                            : 'border-yellow-400/60 bg-yellow-500/10 hover:bg-yellow-500/20'
                        } ${onPositionClick && onPlayerRemove ? 'cursor-pointer' : 'cursor-default'}`}
                        onClick={() => {
                          if (!onPositionClick) return;
                          onPositionClick('LEGEND');
                        }}
                        title={legendCard ? 'Click per cambiare Legend' : 'Click per selezionare Legend'}
                      >
                        <div className="text-[10px] uppercase tracking-wide text-yellow-300 font-bold">Legend</div>
                        <div className="text-xs text-white truncate">{specialCardName(legendCard)}</div>
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
        )}
      </div>
    </>
  );
};

export default FootballField;
